import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PercentIcon from "@mui/icons-material/Percent";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import {Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem} from "@mui/material";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {hasVat} from "./folioLine.util";
import {hasZeroVatStamp, removeZeroVatPatch, zeroVatPatch} from "./folioVat.util";

interface Props {
    line: FolioItem
    /** The reservation's own line: no market code, copy or delete. */
    primary: boolean
    /** Offer the deliberate 0 % VAT - Casablanca needs a VAT on every line. */
    vatRequired: boolean
    onChange: (patch: Partial<FolioItem>) => void
    onDiscount: () => void
    onMarketCode: () => void
    onDuplicate: () => void
    onRemove: () => void
}

/** The row's secondary actions. */
const FolioLineMenu = ({line, primary, vatRequired, onChange, onDiscount, onMarketCode, onDuplicate, onRemove}: Props) => {
    const {t} = useTranslation();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const stamped = hasZeroVatStamp(line);
    const offerZeroVat = vatRequired && !hasVat(line);

    const run = (action: () => void) => () => {
        setAnchor(null);
        action();
    };

    return (
        <>
            <IconButton
                size={'small'}
                aria-label={t('Line options')}
                aria-haspopup={'menu'}
                onClick={event => setAnchor(event.currentTarget)}
                sx={{width: 28, height: 28, p: 0}}
            >
                <MoreVertIcon fontSize={'small'}/>
            </IconButton>

            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                <MenuItem onClick={run(onDiscount)}>
                    <ListItemIcon><PercentIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{line.discounts?.[0] ? t('Edit Discount') : t('Add Discount')}</ListItemText>
                </MenuItem>
                {!primary && (
                    <MenuItem onClick={run(onMarketCode)}>
                        <ListItemIcon><LocalOfferOutlinedIcon fontSize={'small'}/></ListItemIcon>
                        <ListItemText>{t('Market code (SKU)')}</ListItemText>
                    </MenuItem>
                )}
                {(offerZeroVat || stamped) && (
                    <MenuItem onClick={run(() => onChange(stamped ? removeZeroVatPatch(line) : zeroVatPatch(line)))}>
                        <ListItemIcon><ReceiptLongOutlinedIcon fontSize={'small'}/></ListItemIcon>
                        <ListItemText>{stamped ? t('Remove 0% VAT') : t('Charge 0% VAT')}</ListItemText>
                    </MenuItem>
                )}
                {!primary && <Divider/>}
                {!primary && (
                    <MenuItem onClick={run(onDuplicate)}>
                        <ListItemIcon><ContentCopyIcon fontSize={'small'}/></ListItemIcon>
                        <ListItemText>{t('Duplicate')}</ListItemText>
                    </MenuItem>
                )}
                {!primary && (
                    <MenuItem onClick={run(onRemove)} sx={{color: 'error.main'}}>
                        <ListItemIcon><DeleteIcon fontSize={'small'} color={'error'}/></ListItemIcon>
                        <ListItemText>{t('Delete')}</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default FolioLineMenu;
